import { Sort } from '@fgb/core';
import { pipe, map } from 'rxjs';

/** A list of helpful regular expressions to keep validation consistent throughout the site. */
class RegexUS {
    /** Validates an email address. */
    //prettier-ignore
    static { this.email = new RegExp('^(([^<>()\\[\\]\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$'); }
    /** Validates a post code */
    static { this.postCode = new RegExp('^\\d{5}$'); }
    /** Validates a gender input field. */
    static { this.gender = new RegExp('^(M|F|O)$'); }
    /** Validates a number input field */
    static { this.number = new RegExp('^\\d+$'); }
    /** Validates currency for transfers */
    static { this.currency = new RegExp('^[0-9]+(.[0-9]{1,2})?$'); }
}
class RegexUK {
    /** Validates an email address. */
    //prettier-ignore
    static { this.email = new RegExp('^(([^<>()\\[\\]\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$'); }
    /** Validates a post code */
    static { this.postCode = new RegExp('[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}'); }
    /** Validates a gender input field. */
    static { this.gender = new RegExp('^(M|F|O)$'); }
    /** Validates a number input field */
    static { this.number = new RegExp('^\\d+$'); }
    /** Validates currency for transfers */
    static { this.currency = new RegExp('^[0-9]+(.[0-9]{1,2})?$'); }
}

class OwlCarouselSettings {
    static { this.Margin = 15; }
    static { this.Autoplay = true; }
    static { this.AutoplayTimeout = 7000; }
    static { this.AutoplaySpeed = 700; }
}

/**
 * Sort an array of objects by a property or a function
 * @example
 *  const items = [{ name: 'in', lastname: 'wakwak' }, { name: 'sik', lastname: 'kuyamad }];
 *  items.sort(sortBy('name'));
 *
 *  you can also pass a function to sort the array
 *  @example
 *  items.sort(sortBy((a, b) => a.name.localeCompare(b.name)));
 *
 *  you can also pass a second parameter to specify the order of the sort
 *  @example
 *  sort by name in descending order
 *  items.sort(sortBy('name', 'desc'));
 *
 *  @example
 *  for sorting by date, add the type option because sometimes it fails when validating the string using luxon
 *  items.sort(sortBy('date', 'asc', { type: 'date' }));
 *
 *  OR
 *
 *  use
 *  @see {@link sortDate}
 *
 *  for precision use the options parameter to specify the type of the property you want to sort
 */
function sortBy(prop, order = 'asc', options) {
    return Sort.by(prop, order, options);
}
function sortMultiple(...args) {
    return Sort.multiple(...args);
}
/**
 * Luxon date validation sometimes fails esp. when using DateTime.fromISO('10').isValid -> true
 *
 * @example
 * items.sort(sortDate('StartDate', 'asc'));
 */
function sortDate(prop, order = 'asc') {
    return Sort.byDate(prop, order);
}
/**
 * Pipe operator for observable for sorting an array of objects by passing parameter function or property name. default order is ascending
 * @example
 *  this.items$ = this.lottosQuery.selectAll()
 *   .pipe(
 *      sortPipe('name')
 *   );
 *
 * you can also pass a second parameter to specify the order of the sort
 * @example
 * this.items$ = this.lottosQuery.selectAll()
 *  .pipe(
 *    sortPipe(
 *     sortBy('name', 'desc'),
 *     sortBy('date', 'asc', { type: 'date' })
 *    )
 *  )
 */
function sortPipe(...args) {
    return pipe(map((items) => items.sort(sortMultiple(...args))));
}

/**
 * Get the operation to compare two values
 * @example
 * getOperation(1, 2, '>') // false
 * getOperation(1, 2, '<') // true
 * getOperation(1, 2, '=') // false
 * getOperation(1, 2, '!=') // true
 * getOperation('John', 'john', 'like') // true
 */
function getOperation(a, b, operator) {
    switch (operator) {
        case '>':
            return a > b;
        case '<':
            return a < b;
        case '=':
            return a === b;
        case '!=':
            return a !== b;
        case 'like':
            return a.toString().toLowerCase().includes(b.toString().toLowerCase());
        default:
            return a === b;
    }
}
;
/**
 * Filter an array of objects by passing an object with the properties you want to filter
 * @example
 *   items.filter(filterBy({ name: 'John', age: 20 }));
 */
function filterBy(filters, operator = '=') {
    return (item) => Object.keys(filters).every(key => getOperation(item[key], filters[key], operator));
}
function isLogicalOperator(obj) {
    return obj && obj.operator;
}
function filterMultiple(...args) {
    return (item) => {
        let operators = [];
        return args.reduce((acc, filter) => {
            if (isLogicalOperator(filter)) {
                operators.push(filter.operator);
                return acc;
            }
            else {
                if (operators.length > 0) {
                    if (operators.pop() === 'or') {
                        return acc || filter(item);
                    }
                }
            }
            return acc && filter(item);
        }, true);
    };
}

/**
 * Generated bundle index. Do not edit.
 */

export { OwlCarouselSettings, RegexUK, RegexUS, filterBy, filterMultiple, getOperation, isLogicalOperator, sortBy, sortDate, sortMultiple, sortPipe };
